import { Plus, Tag as TagIcon, FolderOpen, List as ListIcon, Edit2, Trash2, RotateCcw, Sparkles, PlayCircle } from "lucide-react";
import { DailyRoutineItem, DailyRoutineFormModal, DailyRoutineDeleteConfirm } from "./components/DailyRoutineComponents";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { LIST_TOOLBAR_BTN, LIST_TOOLBAR_ICON, LIST_TOOLBAR_ROW } from "../../constants/listToolbarButtons";
import Modal from "../../components/ui/Modal";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ApiService } from "../../services/api";
import { useModal } from "../../hooks/useModal";
import checkPermission from "../../utils/permission";
import TodoCategoryComponent from "./components/TodoCategory";
import TodoCategoryForm from "./components/TodoCategoryForm";
import TodoTagForm from "./components/TodoTagForm";
import TodoItemForm from "./components/TodoItemForm";

interface TodoTag {
  id: string;
  name: string;
  color: string;
}

interface TodoItem {
  id: string;
  text: string;
  completed: boolean;
  cancelled: boolean;
  priority: "low" | "medium" | "high" | "critical";
  todoTags: TodoTag[];
  estimatedTimeValue?: number;
  estimatedTimeUnit?: "minutes" | "hours" | "days";
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  todoCategoryId: string;
  isTask?: boolean;
}

interface TodoCategory {
  id: string;
  name: string;
  color: string;
  isTask?: boolean;
}

interface DailyRoutine {
  id: string;
  text: string;
  priority: "low" | "medium" | "high" | "critical";
  color: string;
  completedToday: boolean;
  order: number;
  createdAt: string;
}

export default function TodosPage() {
  const navigate = useNavigate();
  const { showAlert } = useModal();

  const canViewTodos = checkPermission("todo_list");
  const canViewCategories = checkPermission("todo_category_list");
  const canAddCategories = checkPermission("todo_category_add");
  const canUpdateCategories = checkPermission("todo_category_update");
  const canDeleteCategories = checkPermission("todo_category_delete");
  const canViewTags = checkPermission("todo_tag_list");
  const canAddTags = checkPermission("todo_tag_add");
  const canUpdateTags = checkPermission("todo_tag_update");
  const canDeleteTags = checkPermission("todo_tag_delete");
  const canViewItems = checkPermission("todo_item_view");
  const canAddItems = checkPermission("todo_item_add");
  const canUpdateItems = checkPermission("todo_item_update");
  const canDeleteItems = checkPermission("todo_item_delete");
  const canToggleItems = checkPermission("todo_item_toggle");
  const canViewDailyRoutines = checkPermission("todo_daily_routine_list");
  const canAddDailyRoutine = checkPermission("todo_daily_routine_add");
  const canUpdateDailyRoutine = checkPermission("todo_daily_routine_update");
  const canDeleteDailyRoutine = checkPermission("todo_daily_routine_delete");
  const canToggleDailyRoutine = checkPermission("todo_daily_routine_toggle");
  const [categories, setCategories] = useState<TodoCategory[]>([]);
  const [availableTags, setAvailableTags] = useState<TodoTag[]>([]);
  const [todoItems, setTodoItems] = useState<TodoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<{
    categoryId: string;
    itemId: string;
  } | null>(null);
  const [editText, setEditText] = useState("");
  const [editPriority, setEditPriority] = useState<"low" | "medium" | "high" | "critical">("low");
  const [editTags, setEditTags] = useState<TodoTag[]>([]);
  const [editEstTime, setEditEstTime] = useState<number>(0);
  const [editEstUnit, setEditEstUnit] = useState<"minutes" | "hours" | "days">("hours");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newItemText, setNewItemText] = useState("");
  const [newItemPriority, setNewItemPriority] = useState<"low" | "medium" | "high" | "critical">("low");
  const [newItemTags, setNewItemTags] = useState<TodoTag[]>([]);
  const [newItemEstTime, setNewItemEstTime] = useState<number>(0);
  const [newItemEstUnit, setNewItemEstUnit] = useState<"minutes" | "hours" | "days">("hours");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");

  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryColor, setNewCategoryColor] = useState("#3b82f6");
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editCategoryName, setEditCategoryName] = useState("");
  const [editCategoryColor, setEditCategoryColor] = useState("#3b82f6");
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);

  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState("#3b82f6");
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [editTagName, setEditTagName] = useState("");
  const [editTagColor, setEditTagColor] = useState("#3b82f6");
  const [showEditTagModal, setShowEditTagModal] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: 'category' | 'tag' | 'item' | 'routine';
    id: string;
    categoryId?: string;
    name: string;
  } | null>(null);

  const [dailyRoutines, setDailyRoutines] = useState<DailyRoutine[]>([]);
  const [routineLoading, setRoutineLoading] = useState(false);
  const [showAddRoutineModal, setShowAddRoutineModal] = useState(false);
  const [showEditRoutineModal, setShowEditRoutineModal] = useState(false);
  const [editingRoutineId, setEditingRoutineId] = useState<string | null>(null);
  const [routineText, setRoutineText] = useState('');
  const [routinePriority, setRoutinePriority] = useState<'low' | 'medium' | 'high' | 'critical'>('low');
  const [routineColor, setRoutineColor] = useState('#3b82f6');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const promises = [];

      if (canViewCategories) promises.push(ApiService.getTodoCategories({ limit: 100 }));
      else promises.push(Promise.resolve({ data: [] }));

      if (canViewTags) promises.push(ApiService.getTodoTags({ limit: 100 }));
      else promises.push(Promise.resolve({ data: [] }));

      if (canViewItems) promises.push(ApiService.getTodoItems({ limit: 1000 }));
      else promises.push(Promise.resolve({ data: [] }));

      if (canViewDailyRoutines) promises.push(ApiService.getDailyRoutines({ limit: 100 }));
      else promises.push(Promise.resolve({ data: [] }));

      const [categoriesRes, tagsRes, itemsRes, routinesRes] = await Promise.all(promises);

      if (canViewCategories) setCategories(categoriesRes?.data || []);
      if (canViewTags) setAvailableTags(tagsRes?.data || []);
      if (canViewItems) setTodoItems(itemsRes?.data || []);
      if (canViewDailyRoutines) setDailyRoutines(routinesRes?.data || []);
    } catch (error) {
      console.error("Error loading todo data:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateDuration = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate.getTime() - startDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;
    return { hours, minutes };
  };

  const handleToggleComplete = async (categoryId: string, itemId: string) => {
    if (!canToggleItems) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Görev durumunu değiştirme yetkiniz bulunmuyor.",
        type: "error",
      });
      return;
    }

    const item = todoItems.find((i) => i.id === itemId);
    if (item?.isTask) {
      await showAlert({
        title: "İşlem Yapılamıyor",
        message: "Görevler modülü TODO'larının durumu değiştirilemez. Lütfen Görevler modülünü kullanın.",
        type: "warning",
      });
      return;
    }
    try {
      const updated = await ApiService.toggleTodoComplete(itemId);
      setTodoItems((prev) => prev.map((item) => (item.id === itemId ? updated : item)));
    } catch (error) {
      console.error("Error toggling complete:", error);
    }
  };

  const handleCancelItem = async (categoryId: string, itemId: string) => {
    if (!canToggleItems) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Görev durumunu değiştirme yetkiniz bulunmuyor.",
        type: "error",
      });
      return;
    }

    const item = todoItems.find((i) => i.id === itemId);
    if (item?.isTask) {
      await showAlert({
        title: "İşlem Yapılamıyor",
        message: "Görevler modülü TODO'larının durumu değiştirilemez. Lütfen Görevler modülünü kullanın.",
        type: "warning",
      });
      return;
    }
    try {
      const updated = await ApiService.toggleTodoCancel(itemId);
      setTodoItems((prev) => prev.map((item) => (item.id === itemId ? updated : item)));
    } catch (error) {
      console.error("Error toggling cancel:", error);
    }
  };

  const handleDeleteItem = async (categoryId: string, itemId: string) => {
    if (!canDeleteItems) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Görev silme yetkiniz bulunmuyor.",
        type: "error",
      });
      return;
    }

    const item = todoItems.find((i) => i.id === itemId);
    if (item) {
      if (item.isTask && !item.completed) {
        await showAlert({
          title: "İşlem Yapılamıyor",
          message: "Tamamlanmamış görev TODO'ları silinemez. Lütfen Görevler modülünü kullanın.",
          type: "warning",
        });
        return;
      }
      setDeleteConfirm({
        type: "item",
        id: itemId,
        categoryId: categoryId,
        name: item.text,
      });
    }
  };

  const handleEditItem = async (categoryId: string, itemId: string) => {
    if (!canUpdateItems) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Görev düzenleme yetkiniz bulunmuyor.",
        type: "error",
      });
      return;
    }

    const item = todoItems.find((i) => i.id === itemId);
    if (item) {
      if (item.isTask) {
        await showAlert({
          title: "İşlem Yapılamıyor",
          message: "Görevler modülü üzerinden oluşturulan TODO'lar düzenlenemez. Lütfen Görevler modülünü kullanın.",
          type: "warning",
        });
        return;
      }
      setEditingItem({ categoryId, itemId });
      setEditText(item.text);
      setEditPriority(item.priority);
      setEditTags(item.todoTags || []);
      setEditEstTime(item.estimatedTimeValue || 0);
      setEditEstUnit(item.estimatedTimeUnit || "hours");
      setShowEditModal(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;

    try {
      const updated = await ApiService.updateTodoItem(editingItem.itemId, {
        text: editText,
        priority: editPriority,
        tagIds: editTags.map((t) => t.id),
        estimatedTimeValue: editEstTime > 0 ? editEstTime : undefined,
        estimatedTimeUnit: editEstTime > 0 ? editEstUnit : undefined,
      });

      setTodoItems((prev) => prev.map((item) => (item.id === editingItem.itemId ? updated : item)));
      setEditingItem(null);
      setShowEditModal(false);
      await loadData();
    } catch (error) {
      console.error("Error updating item:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditText("");
    setEditPriority("low");
    setEditTags([]);
    setEditEstTime(0);
    setEditEstUnit("hours");
    setShowEditModal(false);
  };

  const handleAddItem = async () => {
    if (!canAddItems) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Görev ekleme yetkiniz bulunmuyor.",
        type: "error",
      });
      return;
    }

    if (!newItemText.trim() || !selectedCategoryId) return;

    try {
      const created = await ApiService.createTodoItem({
        text: newItemText,
        todoCategoryId: selectedCategoryId,
        priority: newItemPriority,
        estimatedTimeValue: newItemEstTime > 0 ? newItemEstTime : undefined,
        estimatedTimeUnit: newItemEstTime > 0 ? newItemEstUnit : undefined,
        tagIds: newItemTags.map((t) => t.id),
      });

      setTodoItems((prev) => [...prev, created]);
      setShowAddModal(false);
      setNewItemText("");
      setNewItemPriority("low");
      setNewItemTags([]);
      setNewItemEstTime(0);
      setNewItemEstUnit("hours");
      setSelectedCategoryId("");
      await loadData();
    } catch (error) {
      console.error("Error creating item:", error);
    }
  };

  const handleQuickAddItem = async (categoryId: string) => {
    if (!canAddItems) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Görev ekleme yetkiniz bulunmuyor.",
        type: "error",
      });
      return;
    }

    const category = categories.find((cat) => cat.id === categoryId);
    if (category?.isTask) {
      await showAlert({
        title: "İşlem Yapılamıyor",
        message: "Görevler modülü kategorilerine TODO eklenemez. Lütfen Görevler modülünü kullanın.",
        type: "warning",
      });
      return;
    }
    setSelectedCategoryId(categoryId);
    setShowAddModal(true);
  };

  const handleAddCategory = async () => {
    if (!canAddCategories) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Kategori ekleme yetkiniz bulunmuyor.",
        type: "error",
      });
      return;
    }

    if (!newCategoryName.trim()) return;

    if (newCategoryName.trim().toLowerCase() === "görev") {
      await showAlert({
        title: "Geçersiz Kategori Adı",
        message: "'Görev' adı sistem tarafından kullanıldığı için kategori adı olarak kullanılamaz. Lütfen farklı bir ad seçin.",
        type: "warning",
      });
      return;
    }

    try {
      const created = await ApiService.createTodoCategory({
        name: newCategoryName,
        color: newCategoryColor,
      });

      setCategories((prev) => [...prev, created]);
      setNewCategoryName("");
      setNewCategoryColor("#3b82f6");
      await loadData();
    } catch (error) {
      console.error("Error creating category:", error);
    }
  };

  const handleEditCategory = async (categoryId: string) => {
    if (!canUpdateCategories) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Kategori düzenleme yetkiniz bulunmuyor.",
        type: "error",
      });
      return;
    }

    const category = categories.find((cat) => cat.id === categoryId);
    if (category) {
      if (category.isTask) {
        await showAlert({
          title: "İşlem Yapılamıyor",
          message: "Görevler modülü kategorileri düzenlenemez. Lütfen Görevler modülünü kullanın.",
          type: "warning",
        });
        return;
      }
      setEditingCategory(categoryId);
      setEditCategoryName(category.name);
      setEditCategoryColor(category.color);
      setShowEditCategoryModal(true);
    }
  };

  const handleSaveCategoryEdit = async () => {
    if (!editingCategory || !editCategoryName.trim()) return;

    if (editCategoryName.trim().toLowerCase() === "görev") {
      await showAlert({
        title: "Geçersiz Kategori Adı",
        message: "'Görev' adı sistem tarafından kullanıldığı için kategori adı olarak kullanılamaz. Lütfen farklı bir ad seçin.",
        type: "warning",
      });
      return;
    }

    try {
      const updated = await ApiService.updateTodoCategory(editingCategory, {
        name: editCategoryName,
        color: editCategoryColor,
      });

      setCategories((prev) => prev.map((cat) => (cat.id === editingCategory ? updated : cat)));
      setEditingCategory(null);
      setEditCategoryName("");
      setEditCategoryColor("#3b82f6");
      setShowEditCategoryModal(false);
      await loadData();
    } catch (error) {
      console.error("Error updating category:", error);
    }
  };

  const handleCancelCategoryEdit = () => {
    setEditingCategory(null);
    setEditCategoryName("");
    setEditCategoryColor("#3b82f6");
    setShowEditCategoryModal(false);
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!canDeleteCategories) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Kategori silme yetkiniz bulunmuyor.",
        type: "error",
      });
      return;
    }

    const category = categories.find((cat) => cat.id === categoryId);
    if (category) {
      if (category.isTask) {
        await showAlert({
          title: "İşlem Yapılamıyor",
          message: "Görevler modülü kategorileri silinemez. Lütfen Görevler modülünü kullanın.",
          type: "warning",
        });
        return;
      }
      setDeleteConfirm({
        type: "category",
        id: categoryId,
        name: category.name,
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      if (deleteConfirm.type === "category") {
        await ApiService.deleteTodoCategory(deleteConfirm.id);
        setCategories((prev) => prev.filter((cat) => cat.id !== deleteConfirm.id));
        setTodoItems((prev) => prev.filter((item) => item.todoCategoryId !== deleteConfirm.id));
      } else if (deleteConfirm.type === "tag") {
        await ApiService.deleteTodoTag(deleteConfirm.id);
        setAvailableTags((prev) => prev.filter((tag) => tag.id !== deleteConfirm.id));
        setTodoItems((prev) =>
          prev.map((item) => ({
            ...item,
            todoTags: item.todoTags.filter((tag) => tag.id !== deleteConfirm.id),
          }))
        );
      } else if (deleteConfirm.type === "item") {
        await ApiService.deleteTodoItem(deleteConfirm.id);
        setTodoItems((prev) => prev.filter((item) => item.id !== deleteConfirm.id));
      } else if (deleteConfirm.type === "routine") {
        await ApiService.deleteDailyRoutine(deleteConfirm.id);

        setDailyRoutines((prev) => prev.filter((r) => r.id !== deleteConfirm.id));
      }

      setDeleteConfirm(null);
      await loadData();
    } catch (error) {
      console.error("Error deleting:", error);
    }
  };

  const handleAddTag = async () => {
    if (!canAddTags) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Etiket ekleme yetkiniz bulunmuyor.",
        type: "error",
      });
      return;
    }

    if (!newTagName.trim()) return;

    try {
      const created = await ApiService.createTodoTag({
        name: newTagName,
        color: newTagColor,
      });

      setAvailableTags((prev) => [...prev, created]);
      setNewTagName("");
      setNewTagColor("#3b82f6");
      await loadData();
    } catch (error) {
      console.error("Error creating tag:", error);
    }
  };

  const handleEditTag = (tagId: string) => {
    if (!canUpdateTags) {
      showAlert({
        title: "Yetki Hatası",
        message: "Etiket düzenleme yetkiniz bulunmuyor.",
        type: "error",
      });
      return;
    }

    const tag = availableTags.find((t) => t.id === tagId);
    if (tag) {
      setEditingTag(tagId);
      setEditTagName(tag.name);
      setEditTagColor(tag.color);
      setShowEditTagModal(true);
    }
  };

  const handleSaveTagEdit = async () => {
    if (!editingTag || !editTagName.trim()) return;

    try {
      const updated = await ApiService.updateTodoTag(editingTag, {
        name: editTagName,
        color: editTagColor,
      });

      setAvailableTags((prev) => prev.map((tag) => (tag.id === editingTag ? updated : tag)));

      setTodoItems((prev) =>
        prev.map((item) => ({
          ...item,
          todoTags: item.todoTags.map((tag) => (tag.id === editingTag ? updated : tag)),
        }))
      );

      setEditingTag(null);
      setEditTagName("");
      setEditTagColor("#3b82f6");
      setShowEditTagModal(false);
      await loadData();
    } catch (error) {
      console.error("Error updating tag:", error);
    }
  };

  const handleCancelTagEdit = () => {
    setEditingTag(null);
    setEditTagName("");
    setEditTagColor("#3b82f6");
    setShowEditTagModal(false);
  };

  const handleDeleteTag = (tagId: string) => {
    if (!canDeleteTags) {
      showAlert({
        title: "Yetki Hatası",
        message: "Etiket silme yetkiniz bulunmuyor.",
        type: "error",
      });
      return;
    }

    const tag = availableTags.find((t) => t.id === tagId);
    if (tag) {
      setDeleteConfirm({
        type: "tag",
        id: tagId,
        name: tag.name,
      });
    }
  };

  const toggleTag = (tag: TodoTag, currentTags: TodoTag[], setter: (tags: TodoTag[]) => void) => {
    if (currentTags.find((t) => t.id === tag.id)) {
      setter(currentTags.filter((t) => t.id !== tag.id));
    } else {
      setter([...currentTags, tag]);
    }
  };

  const handleAddRoutine = async () => {
    if (!canAddDailyRoutine) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Rutin ekleme yetkiniz bulunmuyor.",
        type: "error",
      });
      return;
    }

    if (!routineText.trim()) return;

    try {
      setRoutineLoading(true);
      const created = await ApiService.createDailyRoutine({
        text: routineText,
        priority: routinePriority,
        color: routineColor,
      });

      setDailyRoutines((prev) => [...prev, created]);
      setShowAddRoutineModal(false);
      setRoutineText("");
      setRoutinePriority("low");
      setRoutineColor("#3b82f6");
    } catch (error) {
      console.error("Error creating routine:", error);
    } finally {
      setRoutineLoading(false);
    }
  };

  const handleEditRoutine = (routineId: string) => {
    const routine = dailyRoutines.find((r) => r.id === routineId);
    if (routine) {
      setEditingRoutineId(routineId);
      setRoutineText(routine.text);
      setRoutinePriority(routine.priority);
      setRoutineColor(routine.color);
      setShowEditRoutineModal(true);
    }
  };

  const handleSaveRoutineEdit = async () => {
    if (!editingRoutineId || !routineText.trim()) return;

    try {
      setRoutineLoading(true);
      const updated = await ApiService.updateDailyRoutine(editingRoutineId, {
        text: routineText,
        priority: routinePriority,
        color: routineColor,
      });

      setDailyRoutines((prev) =>
        prev.map((r) => (r.id === editingRoutineId ? updated : r))
      );
      setShowEditRoutineModal(false);
      setEditingRoutineId(null);
      setRoutineText("");
      setRoutinePriority("low");
      setRoutineColor("#3b82f6");
    } catch (error) {
      console.error("Error updating routine:", error);
    } finally {
      setRoutineLoading(false);
    }
  };

  const handleDeleteRoutine = (routineId: string) => {
    const routine = dailyRoutines.find((r) => r.id === routineId);
    if (routine) {
      setDeleteConfirm({
        type: "routine",
        id: routineId,
        name: routine.text,
      });
    };
  };

  const handleToggleRoutineComplete = async (routineId: string) => {
    if (!canToggleDailyRoutine) {
      await showAlert({
        title: "Yetki Hatası",
        message: "Rutin durumunu değiştirme yetkiniz bulunmuyor.",
        type: "error",
      });

      return;
    };

    try {
      const updated = await ApiService.toggleDailyRoutineComplete(routineId);

      setDailyRoutines((prev) =>
        prev.map((r) => (r.id === routineId ? updated : r))
      );
    } catch (error) {
      console.error("Error toggling routine:", error);
    }
  };

  const handleGenerateSmartSuggestions = async () => {
    if (!canAddItems) return;

    try {
      setRoutineLoading(true);
      const result = await ApiService.generateSmartRoutineSuggestions();

      if (result.success && result.suggestions.length > 0) {
        await showAlert({
          title: 'Başarılı',
          message: result.message,
          type: 'success',
        });

        loadData();
      } else {
        await showAlert({
          title: 'Bilgi',
          message: result.message || 'Öneri bulunamadı.',
          type: 'info',
        });
      };
    } catch (error: any) {
      console.error('Error generating suggestions:', error);

      await showAlert({
        title: 'Hata',
        message: error?.message || 'Öneriler oluşturulurken bir hata oluştu.',
        type: 'error',
      });
    } finally {
      setRoutineLoading(false);
    }
  };

  const handleGenerateVideoSuggestions = async () => {
    if (!canAddItems) return;

    try {
      setRoutineLoading(true);
      const result = await ApiService.generateVideoWatchingSuggestions();

      if (result.success && result.suggestions.length > 0) {
        await showAlert({
          title: 'Başarılı',
          message: result.message,
          type: 'success',
        });

        loadData();
      } else {
        await showAlert({
          title: 'Bilgi',
          message: result.message || 'İzlenecek video veya hata yapılan konu bulunamadı.',
          type: 'info',
        });
      };
    } catch (error: any) {
      console.error('Error generating video suggestions:', error);

      await showAlert({
        title: 'Hata',
        message: error?.message || 'Video önerileri oluşturulurken bir hata oluştu.',
        type: 'error',
      });
    } finally {
      setRoutineLoading(false);
    }
  };

  const renderCategory = useCallback(
    (category: TodoCategory) => {
      return (
        <TodoCategoryComponent
          key={category.id}
          category={category}
          todoItems={todoItems}
          onToggleComplete={handleToggleComplete}
          onCancelItem={handleCancelItem}
          onEditItem={handleEditItem}
          onDeleteItem={handleDeleteItem}
          onQuickAddItem={handleQuickAddItem}
          canAddItems={canAddItems}
          canUpdateItems={canUpdateItems}
          canDeleteItems={canDeleteItems}
          canToggleItems={canToggleItems}
        />
      );
    },
    [
      todoItems,
      handleToggleComplete,
      handleCancelItem,
      handleEditItem,
      handleDeleteItem,
      handleQuickAddItem,
      canAddItems,
      canUpdateItems,
      canDeleteItems,
      canToggleItems,
    ]
  );

  const tasksTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Görevler",
      path: "todos/tasks",
      tabId: "tasks",
      actions: canAddItems ? (
        <div className={LIST_TOOLBAR_ROW}>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            disabled={categories.length === 0}
            className={`${LIST_TOOLBAR_BTN} bg-green-600 text-white hover:bg-green-700 border-green-600 disabled:opacity-50 disabled:cursor-not-allowed`}
            title={categories.length === 0 ? "Önce kategori oluşturun" : ""}>
            <Plus className={LIST_TOOLBAR_ICON} />
            <span>Yeni Görev</span>
          </button>
        </div>
      ) : undefined,
      customComponent: (
        <div className="h-full overflow-y-auto p-4 custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-sm text-gray-400">Yükleniyor...</p>
            </div>
          ) : categories.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <ListIcon className="w-16 h-16 mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">Henüz kategori eklenmemiş</p>
              <p className="text-sm text-gray-400 mb-6 text-center max-w-md">
                Görev ekleyebilmek için önce "Kategoriler" sekmesinden kategori oluşturun
              </p>
            </div>
          ) : (
            categories.sort((a, b) => a.name.localeCompare(b.name, "tr")).map((category) => renderCategory(category))
          )}
        </div>
      ),
    }),
    [categories, renderCategory, loading]
  );

  const categoriesTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Kategoriler",
      path: "todos/categories",
      tabId: "categories",
      customComponent: (
        <div className="h-full overflow-y-auto p-4 custom-scrollbar">
          <TodoCategoryForm
            categories={categories}
            newCategoryName={newCategoryName}
            setNewCategoryName={setNewCategoryName}
            newCategoryColor={newCategoryColor}
            setNewCategoryColor={setNewCategoryColor}
            onAddCategory={handleAddCategory}
            canAdd={canAddCategories}
          />

          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Kategoriler ({categories.length})</h3>
            {categories.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Henüz kategori yok</p>
                <p className="text-gray-400 text-xs mt-1">Yukarıdan kategori ekleyerek başlayın</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                {categories
                  .sort((a, b) => a.name.localeCompare(b.name, "tr"))
                  .map((category) => (
                    <div key={category.id} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow bg-white">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <div className="w-3 h-3 rounded flex-shrink-0" style={{ backgroundColor: category.color }} />
                          <span className="font-medium text-sm text-gray-900 truncate">{category.name}</span>
                        </div>
                        <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                          {todoItems.filter((item) => item.todoCategoryId === category.id).length}
                        </span>
                      </div>
                      {!category.isTask ? (
                        <div className="flex gap-1.5">
                          {canUpdateCategories && (
                            <button
                              onClick={() => handleEditCategory(category.id)}
                              className="flex-1 px-2 py-1.5 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 inline-flex items-center justify-center gap-1">
                              <Edit2 className="w-3 h-3" />
                              <span className="hidden xs:inline">Düzenle</span>
                            </button>
                          )}
                          {canDeleteCategories && (
                            <button
                              onClick={() => handleDeleteCategory(category.id)}
                              className="flex-1 px-2 py-1.5 bg-red-50 text-red-700 rounded text-xs hover:bg-red-100 inline-flex items-center justify-center gap-1">
                              <Trash2 className="w-3 h-3" />
                              <span className="hidden xs:inline">Sil</span>
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-1">
                          <span className="text-xs text-purple-600 font-medium">Görevler Modülü Kategorisi</span>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      ),
    }),
    [categories, newCategoryName, newCategoryColor, todoItems]
  );

  const tagsTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Etiketler",
      path: "todos/tags",
      tabId: "tags",
      customComponent: (
        <div className="h-full overflow-y-auto p-4 custom-scrollbar">
          <TodoTagForm
            tags={availableTags}
            newTagName={newTagName}
            setNewTagName={setNewTagName}
            newTagColor={newTagColor}
            setNewTagColor={setNewTagColor}
            onAddTag={handleAddTag}
            canAdd={canAddTags}
          />

          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Etiketler ({availableTags.length})</h3>
            {availableTags.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <TagIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Henüz etiket yok</p>
                <p className="text-gray-400 text-xs mt-1">Yukarıdan etiket ekleyerek başlayın</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                {availableTags
                  .sort((a, b) => a.name.localeCompare(b.name, "tr"))
                  .map((tag) => (
                    <div key={tag.id} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow bg-white">
                      <div className="flex items-center gap-2 mb-2 min-w-0">
                        <div className="w-3 h-3 rounded flex-shrink-0" style={{ backgroundColor: tag.color }} />
                        <span className="font-medium text-sm text-gray-900 truncate">{tag.name}</span>
                      </div>
                      <div className="flex gap-1.5">
                        {canUpdateTags && (
                          <button
                            onClick={() => handleEditTag(tag.id)}
                            className="flex-1 px-2 py-1.5 bg-purple-50 text-purple-700 rounded text-xs hover:bg-purple-100 inline-flex items-center justify-center gap-1">
                            <Edit2 className="w-3 h-3" />
                            <span className="hidden xs:inline">Düzenle</span>
                          </button>
                        )}
                        {canDeleteTags && (
                          <button
                            onClick={() => handleDeleteTag(tag.id)}
                            className="flex-1 px-2 py-1.5 bg-red-50 text-red-700 rounded text-xs hover:bg-red-100 inline-flex items-center justify-center gap-1">
                            <Trash2 className="w-3 h-3" />
                            <span className="hidden xs:inline">Sil</span>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      ),
    }),
    [availableTags, newTagName, newTagColor]
  );

  const dailyRoutineTab: TabItemProps = useMemo(
    () => ({
      type: "custom",
      title: "Günlük Rutin",
      path: "todos/daily-routines",
      tabId: "daily-routines",
      actions: canAddItems ? (
        <div className={LIST_TOOLBAR_ROW}>
          <button
            type="button"
            onClick={handleGenerateSmartSuggestions}
            disabled={routineLoading}
            className={`${LIST_TOOLBAR_BTN} bg-indigo-600 text-white hover:bg-indigo-700 border-indigo-600 disabled:opacity-50`}
            title="Hatalarınıza göre soru çözme hedefleri oluşturur">
            {routineLoading ? (
              <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Sparkles className={LIST_TOOLBAR_ICON} />
            )}
            <span className="hidden sm:inline">Soru Önerisi</span>
          </button>

          <button
            type="button"
            onClick={handleGenerateVideoSuggestions}
            disabled={routineLoading}
            className={`${LIST_TOOLBAR_BTN} bg-pink-600 text-white hover:bg-pink-700 border-pink-600 disabled:opacity-50`}
            title="Hatalarınıza göre video izleme hedefleri oluşturur">
            {routineLoading ? (
              <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <PlayCircle className={LIST_TOOLBAR_ICON} />
            )}
            <span className="hidden sm:inline">Video Önerisi</span>
          </button>

          <button
            type="button"
            onClick={() => setShowAddRoutineModal(true)}
            className={`${LIST_TOOLBAR_BTN} bg-purple-600 text-white hover:bg-purple-700 border-purple-600`}>
            <Plus className={LIST_TOOLBAR_ICON} />
            <span>Yeni Rutin</span>
          </button>
        </div>
      ) : undefined,
      customComponent: (
        <div className="h-full overflow-y-auto p-4 custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
              <p className="text-sm text-gray-400">Yükleniyor...</p>
            </div>
          ) : dailyRoutines.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <RotateCcw className="w-16 h-16 mb-4 text-gray-300" />
              <p className="text-lg font-medium mb-2">Henüz günlük rutin eklenmemiş</p>
              <p className="text-sm text-gray-400 mb-6 text-center max-w-md">
                Günlük rutinler her gün otomatik olarak sıfırlanır. Silmediğiniz sürece her gün yeniden tamamlanması gereken görevler olarak görünürler.
              </p>
              {canAddDailyRoutine && (
                <button
                  onClick={() => setShowAddRoutineModal(true)}
                  className="bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg text-sm font-medium text-white inline-flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  İlk Rutininizi Ekleyin
                </button>
              )}
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-3">

              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <RotateCcw className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-purple-900">Günlük Rutinler</p>
                      <p className="text-xs text-purple-600">Her gün sıfırlanır</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-purple-700">
                      {dailyRoutines.filter((r) => r.completedToday).length}/{dailyRoutines.length}
                    </p>
                    <p className="text-xs text-purple-600">Bugün tamamlanan</p>
                  </div>
                </div>
                <div className="mt-3 h-2 bg-purple-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all duration-500"
                    style={{
                      width: `${(dailyRoutines.filter((r) => r.completedToday).length / dailyRoutines.length) * 100}%`,
                    }}
                  />
                </div>
              </div>

              {canAddDailyRoutine && (
                <div className="flex flex-wrap items-center justify-end gap-3 mb-4">
                  <button
                    onClick={() => setShowAddRoutineModal(true)}
                    className="flex-none bg-purple-600 hover:bg-purple-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-2 shadow-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Yeni Ekle</span>
                  </button>
                </div>
              )}

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y divide-gray-100">

                {dailyRoutines
                  .sort((a, b) => {
                    if (a.completedToday !== b.completedToday) {
                      return a.completedToday ? 1 : -1;
                    }

                    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
                    return priorityOrder[a.priority] - priorityOrder[b.priority];
                  })
                  .map((routine) => (
                    <DailyRoutineItem
                      key={routine.id}
                      routine={routine}
                      onToggleComplete={handleToggleRoutineComplete}
                      onEdit={handleEditRoutine}
                      onDelete={handleDeleteRoutine}
                      canToggle={canToggleDailyRoutine}
                      canUpdate={canUpdateDailyRoutine}
                      canDelete={canDeleteDailyRoutine}
                    />
                  ))}
              </div>
            </div>
          )}
        </div>
      ),
    }),
    [dailyRoutines, loading, canAddItems, canUpdateItems, canDeleteItems, canToggleItems, handleGenerateSmartSuggestions, handleGenerateVideoSuggestions, routineLoading, showAddRoutineModal]
  );

  const tabs = useMemo(() => {
    const availableTabs = [];

    // Görevler İLK sekme = varsayılan görünüm (koç planı kalemleri de burada).
    // Eskiden Günlük Rutin ilk geldiği için sayfa hep rutin görünümüyle açılıyordu.
    if (canViewItems) {
      availableTabs.push(tasksTab);
    }

    if (canViewDailyRoutines) {
      availableTabs.push(dailyRoutineTab);
    }

    if (canViewCategories) {
      availableTabs.push(categoriesTab);
    }

    if (canViewTags) {
      availableTabs.push(tagsTab);
    }

    return availableTabs;
  }, [tasksTab, dailyRoutineTab, categoriesTab, tagsTab, canViewItems, canViewCategories, canViewTags, canViewDailyRoutines]);

  if (!canViewTodos && !canViewItems && !canViewCategories && !canViewTags && !canViewDailyRoutines) {
    return (
      <div className="h-[88vh] bg-gray-50 overflow-hidden flex">
        <div className="flex-1 min-w-0 flex items-center justify-center">
          <div className="text-center text-gray-600">Bu alana erişim yetkiniz bulunmuyor.</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <TabPage title="Yapılacaklar" tabs={tabs} />

      <TodoItemForm
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSelectedCategoryId("");
          setNewItemText("");
          setNewItemPriority("low");
          setNewItemTags([]);
          setNewItemEstTime(0);
          setNewItemEstUnit("hours");
        }}
        title="Yeni Görev Ekle"
        categories={categories}
        availableTags={availableTags}
        text={newItemText}
        setText={setNewItemText}
        priority={newItemPriority}
        setPriority={setNewItemPriority}
        selectedTags={newItemTags}
        setSelectedTags={setNewItemTags}
        estimatedTime={newItemEstTime}
        setEstimatedTime={setNewItemEstTime}
        estimatedUnit={newItemEstUnit}
        setEstimatedUnit={setNewItemEstUnit}
        selectedCategoryId={selectedCategoryId}
        setSelectedCategoryId={setSelectedCategoryId}
        onSubmit={handleAddItem}
        submitText="Ekle"
      />

      <TodoItemForm
        isOpen={showEditModal}
        onClose={handleCancelEdit}
        title="Görevi Düzenle"
        categories={categories}
        availableTags={availableTags}
        text={editText}
        setText={setEditText}
        priority={editPriority}
        setPriority={setEditPriority}
        selectedTags={editTags}
        setSelectedTags={setEditTags}
        estimatedTime={editEstTime}
        setEstimatedTime={setEditEstTime}
        estimatedUnit={editEstUnit}
        setEstimatedUnit={setEditEstUnit}
        selectedCategoryId=""
        setSelectedCategoryId={() => { }}
        onSubmit={handleSaveEdit}
        submitText="Kaydet"
        isEdit={true}
      />

      <Modal isOpen={showEditCategoryModal} onClose={handleCancelCategoryEdit} title="Kategori Düzenle" size="md">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Kategori Adı *</label>
            <input
              type="text"
              value={editCategoryName}
              onChange={(e) => setEditCategoryName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Kategori adı..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Renk</label>
            <div className="flex gap-1.5 sm:gap-2">
              {["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"].map((color) => (
                <button
                  key={color}
                  onClick={() => setEditCategoryColor(color)}
                  className={`w-8 h-8 sm:w-10 sm:h-10 rounded-md transition-all flex-shrink-0 ${editCategoryColor === color ? "ring-2 ring-offset-1 sm:ring-offset-2 ring-gray-600 scale-110" : "hover:scale-105"
                    }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={handleCancelCategoryEdit}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors">
              İptal
            </button>
            <button
              onClick={handleSaveCategoryEdit}
              disabled={!editCategoryName.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              Kaydet
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showEditTagModal} onClose={handleCancelTagEdit} title="Etiket Düzenle" size="md">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Etiket Adı *</label>
            <input
              type="text"
              value={editTagName}
              onChange={(e) => setEditTagName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Etiket adı..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Renk</label>
            <div className="flex gap-1.5 sm:gap-2">
              {["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"].map((color) => (
                <button
                  key={color}
                  onClick={() => setEditTagColor(color)}
                  className={`w-8 h-8 sm:w-10 sm:h-10 rounded-md transition-all flex-shrink-0 ${editTagColor === color ? "ring-2 ring-offset-1 sm:ring-offset-2 ring-gray-600 scale-110" : "hover:scale-105"
                    }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <button
              onClick={handleCancelTagEdit}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-300 transition-colors">
              İptal
            </button>
            <button
              onClick={handleSaveTagEdit}
              disabled={!editTagName.trim()}
              className="px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              Kaydet
            </button>
          </div>
        </div>
      </Modal>

      <DailyRoutineFormModal
        isOpen={showAddRoutineModal}
        onClose={() => {
          setShowAddRoutineModal(false);
          setRoutineText("");
          setRoutinePriority("low");
          setRoutineColor("#3b82f6");
        }}
        title="Yeni Günlük Rutin"
        text={routineText}
        setText={setRoutineText}
        priority={routinePriority}
        setPriority={setRoutinePriority}
        color={routineColor}
        setColor={setRoutineColor}
        onSubmit={handleAddRoutine}
        submitText="Ekle"
        isLoading={routineLoading}
      />

      <DailyRoutineFormModal
        isOpen={showEditRoutineModal}
        onClose={() => {
          setShowEditRoutineModal(false);
          setEditingRoutineId(null);
          setRoutineText("");
          setRoutinePriority("low");
          setRoutineColor("#3b82f6");
        }}
        title="Rutin Düzenle"
        text={routineText}
        setText={setRoutineText}
        priority={routinePriority}
        setPriority={setRoutinePriority}
        color={routineColor}
        setColor={setRoutineColor}
        onSubmit={handleSaveRoutineEdit}
        submitText="Kaydet"
        isLoading={routineLoading}
      />

      <ConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={confirmDelete}
        title={`${deleteConfirm?.type === "category" ? "Kategori" : deleteConfirm?.type === "tag" ? "Etiket" : deleteConfirm?.type === "routine" ? "Rutin" : "Görev"} Sil`}
        message={`"${deleteConfirm?.name}" ${deleteConfirm?.type === "category" ? "kategorisini" : deleteConfirm?.type === "tag" ? "etiketini" : deleteConfirm?.type === "routine" ? "rutinini" : "görevini"
          } silmek istediğinizden emin misiniz?${deleteConfirm?.type === "category"
            ? " Bu kategorideki tüm görevler de silinecektir."
            : deleteConfirm?.type === "tag"
              ? " Bu etiket tüm görevlerden kaldırılacaktır."
              : deleteConfirm?.type === "routine"
                ? " Bu rutin ve tüm tamamlanma geçmişi silinecektir."
                : ""
          }`}
        confirmText="Sil"
        cancelText="İptal"
        confirmButtonVariant="danger"
      />
    </>
  );
}